import { Box, styled, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Table, Tag } from "antd";
import { useSnackbar } from "notistack";
import WorkflowHeaderData from "../eachWorkflow/WorkflowHeaderData";
import { LogApi } from "../../services/LogApi";
import { useAxios } from "../../contexts/AxiosProvider";
import { format, toZonedTime } from "date-fns-tz";

// Styled component for the log container, adjusting the margin and width based on the drawer state

const LogContainer = styled(Box)(({ openDrawer }) => ({
  marginLeft: openDrawer ? 240 : 80,
  width: openDrawer ? "calc(100% - 255px)" : "calc(100% - 85px)",
  height: "calc(100vh - 70px)",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
}));

// Main component for displaying logs
const Log = ({ openDrawer, userData }) => {
  const params = useParams(); // Accessing URL parameters
  const location = useLocation(); // Accessing location object to get state
  const axios = useAxios(); // Custom Axios instance with configurations
  const navigate = useNavigate(); // Navigation hook for programmatic navigation
  const enqueueSnackbar = useSnackbar(); // Snackbar for notifications
  const [loading, setloading] = useState(false); // State for loading indicator
  const [status, setStatus] = useState("");
  const [logExecutionDatas, setlogExecutionDatas] = useState({
    totalLength: 0,
    content: [],
  }); // State for log data
  const [logPageData, setLogPageData] = useState({
    pageNumber: 1,
    pageSize: 5,
  });

  const headerData = {
    firstHeaderText: location.state?.workflow_name,
    secondHeaderText: location.state?.domain_name,
    thirdHeaderText: location.state?.client_name,
    fourthHeaderText: location.state?.engagement_name,
    fifthHeaderText: location.state?.created_by,
    sixthHeaderText: location.state?.created_at,
    seventhHeaderText: location.state?.status,
    domainName: "domainName",
    route: `/dep/workflow/${params.engagementId}/${params.processType}`,
  };

  // Function to fetch log streams
  const fetchLogStreams = async (pageNumber, pageSize, status) => {
    try {
      setloading(true); // Set loading to true before API call
      setLogPageData({
        ...logPageData,
        pageNumber: pageNumber,
        pageSize: pageSize,
      });

      const data = {
        workflow_type: params.workflowType,
        workflow_id: location.state?.workflow_id,
        pageNumber: pageNumber,
        pageSize: pageSize,
        status: status,
      };
      // API call to fetch log data
      const response = await LogApi.getAllLogstreams(axios, data);
      if (response.status === 200) {
        setlogExecutionDatas(response.data); // Set log data if response is successful
      }
    } catch (error) {
      // Handle error scenario
      if (error.response?.status === 500) {
        enqueueSnackbar("500 Internal Server Error!", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }
    } finally {
      setloading(false); // Set loading to false after API call is complete
    }
  };

  // useEffect to call the fetch function when the component mounts
  useEffect(() => {
    fetchLogStreams(logPageData.pageNumber, logPageData.pageSize, status);
  }, [status]);

  // Function to navigate to the log stream details
  const RenderLogStreamComponent = (record) => {
    navigate(
      `/dep/workflow/${params.engagementId}/${params.processType}/logs/${params.workflowType}/${record.log_stream_name}`,
      {
        state: {
          ...userData,
        },
      }
    );
  };

  const formattedDate = (dateString) => {
    if (!dateString) return "";
    const zonedDate = toZonedTime(dateString, "America/New_York");
    return format(zonedDate, "MMM dd, yyyy HH:mm:ss");
  };

  // Columns for the log table
  const LogColumns = [
    {
      title: "Execution Id",
      dataIndex: "execution_id",
      key: "execution_id",
      render: (text) => <a>{text}</a>,
      onCell: (record) => {
        return {
          onClick: () => {
            RenderLogStreamComponent(record); // Call openDrawer function here or pass any necessary data to Engagement component
          },
        };
      },
    },
    {
      title: "Execution Time",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (text, record) => (
        <Typography variant="body1" gutterBottom sx={{ color: "#00043C" }}>
          {formattedDate(new Date(record.timestamp), "yyyy-MM-dd hh:mm a")}
        </Typography>
      ),
    },
    {
      title: "Executed By",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Schedule Type",
      dataIndex: "scheduled_type",
      key: "scheduled_type",
    },
    {
      title: "Execution Type",
      dataIndex: "execution_type",
      key: "execution_type",
      render: (text, { execution_type }) =>
        execution_type === "WORKFLOW_PROCESSING"
          ? "Workflow Processed"
          : "Engagement Processed",
    },
    {
      title: "Execution Status",
      dataIndex: "log_status",
      key: "log_status",
      filters: [
        {
          text: "completed",
          value: "Completed",
        },
        {
          text: "running",
          value: "Running",
        },
        {
          text: "failed",
          value: "Failed",
        },
      ],
      filterMultiple: false,
      onFilter: (value, record) => record.log_status === value,
      render: (_, { log_status }) => (
        <>
          <Tag
            color={
              log_status === "Completed"
                ? "green"
                : log_status === "Failed"
                ? "red"
                : log_status === "Running"
                ? "orange"
                : "gray"
            }
            key={log_status}
          >
            {log_status}
          </Tag>
        </>
      ),
    },
  ];

  const handleLogTableChange = (pagination, filters) => {
    const currentStatus = filters.log_status ? filters.log_status[0] : "";
    setStatus(currentStatus);
    fetchLogStreams(pagination.current, pagination.pageSize, currentStatus);
  };

  return (
    <LogContainer openDrawer={openDrawer}>
      <WorkflowHeaderData data={headerData} />
      <Table
        bordered
        loading={loading}
        columns={LogColumns}
        pagination={{
          hideOnSinglePage: true,
          defaultPageSize: 5,
          total: logExecutionDatas.totalLength,
        }}
        onChange={handleLogTableChange}
        style={{ fontWeight: "640", margin: "20px 20px" }}
        dataSource={logExecutionDatas.content}
      />
    </LogContainer>
  );
};

export default Log;
